import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from "react-datepicker";
import { format } from "date-fns"; 
import es from "date-fns/locale/es"; // Importa el idioma español
registerLocale("es", es); // Registra el idioma

const horariosOdontologas = {
  1: "Lun, Jue 8:00-12:00 hs // Mie, Vie 17:00-20:00 hs",
  2: "Mar, Jue 8:30-13:00 hs // Lun, Mie 15:30-20-30 hs // Jue 17.30-21:00 hs",
  3: "Lun, Mar 15:00-19:00 hs // Jue 17:30-20:00",
  4: "Lun, Mie, Vie 8:30-12:00 hs// Mar-Vie 15:00-18:00",
};

const BannerStart = () => {
  const [odontologas, setOdontologas] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [selectedOdontologa, setSelectedOdontologa] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHorario, setSelectedHorario] = useState('');
  const [dni, setDni] = useState('');
  const [pacienteExistente, setPacienteExistente] = useState(null);
  const [nuevoPaciente, setNuevoPaciente] = useState({ nombre: '', correo: '', telefono: '' });
  const [descripcion, setDescripcion] = useState('');
  const [dniVerificado, setDniVerificado] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  const debounceTimeout = useRef(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/odontologas/`).then((response) => {
      setOdontologas(response.data);
    });
  }, []);

  useEffect(() => {
    if (selectedOdontologa) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/horarios/fechas-disponibles/${selectedOdontologa}`)
        .then((response) => {
          const fechas = response.data.map((item) => {
            const fecha = new Date(item.fecha);
            fecha.setDate(fecha.getDate() + 1); // Sumar 1 al día de la fecha
            return fecha;
          });
          setAvailableDates(fechas);
        })
        .catch((error) => console.error('Error al cargar fechas disponibles:', error));
    } else {
      setAvailableDates([]);
    }
  }, [selectedOdontologa]);

  useEffect(() => {
    if (selectedOdontologa && selectedDate) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/horarios/disponibles/${selectedOdontologa}`, {
          params: { fecha: selectedDate.toISOString().split('T')[0] },
        })
        .then((response) => setHorarios(response.data))
        .catch((error) => console.error('Error al cargar horarios:', error));
    }
  }, [selectedOdontologa, selectedDate]);

  useEffect(() => {
    if (dni.length >= 8) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        buscarPacientePorDNI(dni);
      }, 300);
    }
  }, [dni]);

  const buscarPacientePorDNI = async (dni) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/pacientes/dni/${dni}`);
      setPacienteExistente(response.data);
      setDniVerificado(true); // Activar después de verificar
    } catch {
      setPacienteExistente(null);
      setDniVerificado(true); // Activar después de verificar
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      buscarPacientePorDNI(dni);
    }
  };

  const handleBlur = () => {
    buscarPacientePorDNI(dni);
  };

  const handleDniChange = (e) => {
    setDni(e.target.value);
  };

  const agendarConsulta = async () => {
    const payload = {
      dni,
      id_horario: selectedHorario,
      descripcion,
    };

    if (!pacienteExistente) {
      payload.nombre = nuevoPaciente.nombre;
      payload.correo = nuevoPaciente.correo;
      payload.telefono = nuevoPaciente.telefono;
    }

    try {
      const endpoint = pacienteExistente
        ? `${process.env.REACT_APP_BACKEND_URL}/consultas/existente`
        : `${process.env.REACT_APP_BACKEND_URL}/consultas/nuevo`;

      await axios.post(endpoint, payload);
      setAlert({ show: true, message: 'Consulta agendada, la odontologa se contactará a la brevedad.', type: 'success' });
      // Resetear campos
      setHorarios([]);
      setSelectedOdontologa('');
      setSelectedDate(null);
      setSelectedHorario('');
      setDni('');
      setPacienteExistente(null);
      setNuevoPaciente({ nombre: '', correo: '', telefono: '' });
      setDescripcion('');
      setDniVerificado(false); // Desactivar después de agendar
    } catch (error) {
      setAlert({ show: true, message: 'Error al agendar consulta', type: 'danger' });
    }
  };

  const handleWhatsApp = () => {
    window.open("https://wa.me/543815615279", "_blank");
  };

  const mostrarCamposPaciente = () => {
    if (!selectedOdontologa || !selectedDate || !selectedHorario) return null;

    return (
      <>
        <input
          type="text"
          className="form-control bg-light border-0 mb-3"
          placeholder="DNI"
          value={dni}
          onChange={handleDniChange}
          onKeyDown={handleKeyDown} // Buscar paciente al presionar Enter
          onBlur={handleBlur} // Buscar paciente al salir del campo
        />

        {dniVerificado && (
          <>
            {pacienteExistente ? (
              <div className="alert alert-primary mt-2" role="alert">
                Nos alegramos de verte de vuelta, <strong>{pacienteExistente.nombre_paciente}</strong>!
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Nombre"
                  value={nuevoPaciente.nombre}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, nombre: e.target.value })}
                />
                <input
                  type="email"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Correo"
                  value={nuevoPaciente.correo}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, correo: e.target.value })}
                />
                <input
                  type="text"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Teléfono"
                  value={nuevoPaciente.telefono}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, telefono: e.target.value })}
                />
              </>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div className="container-fluid banner mb-5">
      <div className="container">
        <div className="row gx-0">
          <div className="col-lg-4 bg-dark d-flex flex-column justify-content-center align-items-center p-5">
            <button
              className="btn btn-secondary px-22 py-4 rounded-pill"
              onClick={handleWhatsApp}
            >
              Contactanos por WhatsApp
            </button>
          </div>
          <div className="col-lg-4 bg-primary d-flex flex-column p-5">
            <h3 className="text-white mb-3">Agendá tu turno</h3>

            <select
              className="form-select bg-light border-0 mb-3"
              value={selectedOdontologa}
              onChange={(e) => {
                setSelectedOdontologa(e.target.value);
                setSelectedDate(null); // Resetear fecha seleccionada al cambiar de odontóloga
                setSelectedHorario(''); // Resetear horario seleccionado
              }}
            >
              <option value="">Seleccionar odontóloga</option>
              {odontologas.map((odontologa) => (
                <option key={odontologa.id} value={odontologa.id}>
                  {odontologa.nombre}
                </option>
              ))}
            </select>
    <DatePicker
      selected={selectedDate}
      onChange={(date) => setSelectedDate(date)}
      includeDates={availableDates}
      className="form-control bg-light border-0 mb-3"
      locale="es"
      dateFormat="dd/MM/yyyy"
      minDate={new Date()}
      showPopperArrow={false}
      placeholderText="Seleccionar fecha:"
      renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
        <div className="text-center">
          <button className="custom-header-button" onClick={decreaseMonth}>&lt;</button>
          <div style={{ display: 'inline-block', margin: '0 10px', fontSize: "1.2rem", fontWeight: "bold", color: "black" }}>
            {format(date, "MMMM yyyy", { locale: es })}
          </div>
          <button className="custom-header-button" onClick={increaseMonth}>&gt;</button>
          {selectedOdontologa && (
            <>

              <div style={{ fontSize: "0.8rem", color: "black" }}>
                {horariosOdontologas[selectedOdontologa]
                  ?.split("//")
                  .map((line, index) => (
                    <div key={index}>{line.trim()}</div>
                  )) || "Horarios no disponibles"}
              </div>
            </>
          )}
        </div>
      )}
    />

            <select
              className="form-select bg-light border-0 mb-3"
              value={selectedHorario}
              onChange={(e) => setSelectedHorario(e.target.value)}
            >
              <option value="">Seleccionar horario</option>
              {horarios.map((horario) => (
                <option key={horario.id_horario} value={horario.id_horario}>
                  {horario.hora}
                </option>
              ))}
            </select>

            {mostrarCamposPaciente()}

            <button className="btn btn-light" onClick={agendarConsulta}>
              Agendar
            </button>
            {alert.show && (
              <div className={`alert alert-primary mt-3`} role="alert">
                <span className="closebtn" onClick={() => setAlert({ show: false, message: '', type: '' })}>&times;</span>
                {alert.message}
              </div>
            )}
          </div>
          <div className="col-lg-4 bg-secondary d-flex flex-column p-5">
            <h3 className="text-white mb-3">Encontranos en</h3>
            <h5 className="mb-3 text-white fs-3">
              <i className="fa fa-map-marker text-white me-3"></i>Galería Moreno (Moreno esquina Salas y Valdez) Planta alta, Local 14, Yerba Buena.
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerStart;